import {
  computed,
  inject,
  type InjectionKey,
  provide,
  reactive,
  ref,
} from "vue";

import { useCounterpartyForm } from "@/components/common/useCounterpartyForm";

import type { OfferTermsRequest } from "../solderaInventoryTypes";

export type OfferTerms = ReturnType<typeof createOfferTerms>;

const OFFER_TERMS_KEY: InjectionKey<OfferTerms> = Symbol("offerTerms");

/**
 * Creates the offer terms for one visit to the Soldera Inventory page and hands them to its tabs.
 * They live as long as the layout does, so switching tabs keeps what the buyer entered and every
 * visit starts a fresh offer.
 */
export function provideOfferTerms(): OfferTerms {
  const terms = createOfferTerms();
  provide(OFFER_TERMS_KEY, terms);
  return terms;
}

/** The terms `provideOfferTerms` created for this page. */
export function useOfferTerms(): OfferTerms {
  const terms = inject(OFFER_TERMS_KEY);
  if (!terms)
    throw new Error("useOfferTerms needs provideOfferTerms in an ancestor");
  return terms;
}

/**
 * The buyer's side of an offer, shared by the Spot and Forward tabs: the counterparty (from the
 * shared company search) and the price.
 */
export function createOfferTerms() {
  const {
    counterpartyName,
    counterpartyCountry,
    counterpartyEecsAccountNumber,
    counterpartyEmails,
    counterpartyModel,
    selectedGlobalCompany,
    handleCompanySelected,
  } = useCounterpartyForm();
  // `v-model.number` on a number input writes "" when the field is cleared.
  const unitPrice = ref<number | "">(0);
  const accountEmail = ref("");

  /** The entered price, or null while there is no positive one. */
  const price = computed(() =>
    typeof unitPrice.value === "number" && unitPrice.value > 0
      ? unitPrice.value
      : null
  );
  const hasPrice = computed(() => price.value !== null);
  /** The price the pages list at: rows whose floor it does not clear are left out once it is set. */
  const listingPrice = computed(() => price.value ?? 0);

  /**
   * Records the signed-in buyer's address; it is also the default invoice recipient, which they can
   * change or add to, so an address already typed is kept.
   */
  const seedAccount = (email: string | null | undefined) => {
    accountEmail.value = email ?? "";
    if (!counterpartyEmails.value.trim())
      counterpartyEmails.value = accountEmail.value;
  };

  /** The first reason these terms cannot be sent yet, or null; the backend refuses each of these too. */
  const blockingKey = computed((): string | null => {
    if (!hasPrice.value) return "soldera_inventory.blocked.no_price";
    if (!counterpartyName.value.trim())
      return "soldera_inventory.blocked.no_company";
    return null;
  });

  /** The terms as the offer endpoints take them; only call once `blockingKey` is null. */
  const toRequest = (): OfferTermsRequest => ({
    unit_price: (price.value ?? 0).toFixed(3),
    counterparty_name: counterpartyName.value.trim(),
    counterparty_country: counterpartyCountry.value || null,
    counterparty_eecs_account_number:
      counterpartyEecsAccountNumber.value.trim() || null,
    counterparty_emails: counterpartyEmails.value.trim() || null,
    global_company_id: selectedGlobalCompany.value?.id ?? null,
  });

  return reactive({
    counterpartyModel,
    counterpartyCountry,
    counterpartyEmails,
    handleCompanySelected,
    unitPrice,
    accountEmail,
    hasPrice,
    listingPrice,
    blockingKey,
    seedAccount,
    toRequest,
  });
}
